const SHA256_PATTERN = /^[a-f0-9]{64}$/i;
const PLATFORM_FILE_EXTENSION = { mac: '.dmg', win: '.exe' };
export const PLATFORMS = ['mac', 'win'];
export const PLATFORM_LABELS = { mac: 'macOS', win: 'Windows' };

const ARCH_LABELS = {
  mac: { arm64: 'Apple Silicon', x64: 'Intel', universal: 'Universal' },
  win: { x64: '64-bit', ia32: '32-bit', arm64: 'ARM' },
};

const isNonEmptyString = (value) => typeof value === 'string' && value.trim() !== '';

const parseArtifact = (artifact) => {
  if (!isNonEmptyString(artifact?.name)) return null;
  if (!SHA256_PATTERN.test(artifact.sha256 ?? '')) return null;
  if (!Number.isSafeInteger(artifact.sizeBytes) || artifact.sizeBytes <= 0) return null;
  return { name: artifact.name, sha256: artifact.sha256.toLowerCase(), sizeBytes: artifact.sizeBytes };
};

export const parseManifest = (raw) => {
  if (!isNonEmptyString(raw?.version)) {
    return { ok: false, error: 'Manifest has no version' };
  }
  if (typeof raw.platforms !== 'object' || raw.platforms === null || Array.isArray(raw.platforms)) {
    return { ok: false, error: 'Manifest has no platforms' };
  }

  const platforms = {};
  for (const platform of PLATFORMS) {
    const artifacts = Array.isArray(raw.platforms[platform]?.artifacts)
      ? raw.platforms[platform].artifacts.map(parseArtifact).filter(Boolean)
      : [];
    if (artifacts.length > 0) {
      platforms[platform] = { artifacts, commitSha: raw.platforms[platform].commitSha ?? null };
    }
  }

  return {
    ok: true,
    manifest: { version: raw.version, builtAtUtc: raw.builtAtUtc ?? null, platforms },
  };
};

export const detectPlatform = ({ platform = '', userAgent = '' } = {}) => {
  const signal = `${platform} ${userAgent}`;
  if (/iphone|ipad|ipod|android|cros/i.test(signal)) return null;
  if (/win/i.test(signal)) return 'win';
  if (/mac/i.test(signal)) return 'mac';
  return null;
};

export const detectArch = ({ platform = null, architecture = '', bitness = '', userAgent = '' } = {}) => {
  if (/^arm/i.test(architecture)) return 'arm64';
  if (/^x86/i.test(architecture)) return bitness === '32' ? 'ia32' : 'x64';
  if (platform !== 'win') return null;
  if (/arm64|aarch64/i.test(userAgent)) return 'arm64';
  if (/win64|x64|wow64/i.test(userAgent)) return 'x64';
  return null;
};

export const artifactUrl = ({ manifestUrl, version, name }) => {
  const base = new URL('./', manifestUrl);
  return new URL(`${encodeURIComponent(version)}/${encodeURIComponent(name)}`, base).toString();
};

const artifactArch = (platform, name) =>
  Object.keys(ARCH_LABELS[platform]).find((candidate) => name.toLowerCase().includes(candidate)) ?? null;

const isPortable = (name) => /portable/i.test(name);

const describeArtifact = (platform, name) => {
  const arch = artifactArch(platform, name);
  const archLabel = arch ? ARCH_LABELS[platform][arch] : null;
  const kind = isPortable(name) ? 'Portable' : null;
  const parts = [archLabel && `${archLabel} (${arch})`, kind].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : name;
};

export const buildDownloadView = (manifest, manifestUrl) => {
  const platforms = {};
  for (const platform of PLATFORMS) {
    const extension = PLATFORM_FILE_EXTENSION[platform];
    const downloads = (manifest.platforms[platform]?.artifacts ?? [])
      .filter((artifact) => artifact.name.toLowerCase().endsWith(extension))
      .map((artifact) => {
        const arch = artifactArch(platform, artifact.name);
        return {
          ...artifact,
          label: describeArtifact(platform, artifact.name),
          arch,
          archLabel: arch ? ARCH_LABELS[platform][arch] : null,
          portable: isPortable(artifact.name),
          url: artifactUrl({ manifestUrl, version: manifest.version, name: artifact.name }),
        };
      });
    platforms[platform] = { available: downloads.length > 0, downloads };
  }
  return { version: manifest.version, builtAtUtc: manifest.builtAtUtc, platforms };
};

export const formatSize = (sizeBytes) => `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`;

export const recommendDownload = (view, { platform, arch } = {}) => {
  const installers = (view?.platforms[platform]?.downloads ?? []).filter((download) => !download.portable);
  if (installers.length === 0) return null;
  const match = arch ? installers.find((download) => download.arch === arch) : undefined;
  return { platform, name: (match ?? installers[0]).name, archMatched: Boolean(match) };
};

export const splitDownloads = (downloads, preferredName) => {
  const primary =
    downloads.find((download) => download.name === preferredName) ??
    downloads.find((download) => !download.portable) ??
    downloads[0];
  return { primary, alternatives: downloads.filter((download) => download !== primary) };
};

export const formatReleaseDate = (isoTimestamp, timeZone) => {
  const date = new Date(isoTimestamp ?? '');
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat('en-US', { dateStyle: 'long', timeZone }).format(date);
};

const fetchLiveView = async ({ manifestUrl, fetchImpl, signal }) => {
  const response = await fetchImpl(manifestUrl, { cache: 'no-store', signal });
  if (!response.ok) throw new Error(`Manifest request failed (${response.status})`);
  const parsed = parseManifest(await response.json());
  if (!parsed.ok) throw new Error(parsed.error);
  return buildDownloadView(parsed.manifest, manifestUrl);
};

export const loadDownloadView = async ({ manifestUrl, fallback, fallbackManifestUrl, fetchImpl = fetch, signal }) => {
  try {
    return { source: 'live', view: await fetchLiveView({ manifestUrl, fetchImpl, signal }) };
  } catch (error) {
    if (error.name === 'AbortError') throw error;
    const parsed = parseManifest(fallback);
    if (!parsed.ok) return { source: 'none', view: null };
    return { source: 'fallback', view: buildDownloadView(parsed.manifest, fallbackManifestUrl) };
  }
};
